/**
 * Route error boundary for lazy pages (used by {@link RouteSuspense}).
 *
 * Catches only chunk-load errors and offers a reload; any other error is
 * rethrown, so it behaves exactly as it would without this boundary.
 */
import { Component, type ReactNode } from 'react';
import { isChunkLoadError } from '@/utils/chunkLoadError';

interface ChunkErrorBoundaryProps {
  children: ReactNode;
  /** The error clears when this changes, so navigating away recovers. */
  resetKey: string;
}

interface ChunkErrorBoundaryState {
  error: unknown;
  resetKey: string;
}

export default class ChunkErrorBoundary extends Component<ChunkErrorBoundaryProps, ChunkErrorBoundaryState> {
  state: ChunkErrorBoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: unknown): Partial<ChunkErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ChunkErrorBoundaryProps,
    state: ChunkErrorBoundaryState,
  ): Partial<ChunkErrorBoundaryState> | null {
    return props.resetKey === state.resetKey ? null : { error: null, resetKey: props.resetKey };
  }

  render() {
    const { error } = this.state;
    if (error == null) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    return (
      <div className="min-h-[var(--app-content-height)] flex items-center justify-center px-4 py-12">
        <div className="aurora-panel w-full max-w-md px-8 py-10 text-center" role="alert">
          <h2 className="text-lg font-semibold mb-3 text-aurora-text">This page could not load</h2>
          <p className="text-sm mb-6 text-aurora-text-muted/70">
            brewlytics may have been updated since this tab opened, or the network dropped.
            Reloading fetches the current version.
          </p>
          <button type="button" className="aurora-btn aurora-btn-primary" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }
}
